import React, { useState, useEffect } from 'react';
import { 
  X, Search, Download, Printer, FileText, Calendar, 
  Truck, User, Package, MapPin, Filter, RefreshCw
} from 'lucide-react';
import '../styles/Manifest.css';
import apiClient from '../services/api';
import { useNavigate } from 'react-router-dom';

const ManifestReport = ({ onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Filter state
  const [filters, setFilters] = useState({
    StartDate: new Date().toISOString().slice(0, 10),
    EndDate: new Date().toISOString().slice(0, 10),
    VehicleID: '',
    DriverID: '',
    OriginCityID: '',
    DestinationCityID: '',
    Status: '',
    ConsignmentType: 'LT'
  });
  
  // Report data
  const [manifestData, setManifestData] = useState(null);
  
  // Dropdown data
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [cities, setCities] = useState([]);
  const [courierStatuses, setCourierStatuses] = useState([]);
  
  // Session
  const session = getSessionData();
  
  function getSessionData() {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session:', error);
      return null;
    }
  }
  
  useEffect(() => {
    loadDropdownData();
  }, []);
  
  const loadDropdownData = async () => {
    try {
      const [vehRes, driverRes, cityRes, statusRes] = await Promise.all([
        apiClient.get('/Vehicles.php').catch(e => ({ data: { success: false } })),
        apiClient.get('/Drivers.php').catch(e => ({ data: { success: false } })),
        apiClient.get('/Cities.php').catch(e => ({ data: { success: false } })),
        apiClient.get('/CourierStatus.php').catch(e => ({ data: { success: false } }))
      ]);
      
      if (vehRes.data.success) setVehicles(vehRes.data.data || []);
      if (driverRes.data.success) {
        const activeDrivers = (driverRes.data.data || []).filter(d => d.isactive === 1);
        setDrivers(activeDrivers);
      }
      if (cityRes.data.success) setCities(cityRes.data.data || []);
      if (statusRes.data.success) setCourierStatuses(statusRes.data.data || []);
      
    } catch (err) {
      console.error('Error loading dropdown data:', err);
    }
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };
  
  const handleGenerateReport = async () => {
    if (!filters.StartDate || !filters.EndDate) {
      setError('Please select start and end dates');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await apiClient.get('/ManifestReport.php', { params: filters });
      
      if (response.data.success) {
        setManifestData(response.data.data);
      } else {
        setError(response.data.message || 'Failed to generate report');
      }
    } catch (err) {
      console.error('Error generating report:', err);
      setError('Error generating report: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };
  
  const handlePrint = () => {
    // Set document title with date range
    const originalTitle = document.title;
    const fromDate = new Date(filters.StartDate).toLocaleDateString('en-GB').replace(/\//g, '-');
    const toDate = new Date(filters.EndDate).toLocaleDateString('en-GB').replace(/\//g, '-');
    document.title = `Manifest_${fromDate}_to_${toDate}`;
    
    // Print
    window.print();
    
    // Restore original title
    setTimeout(() => {
      document.title = originalTitle;
    }, 100);
  };
  
  const handleExportCSV = () => {
    if (!manifestData || !manifestData.consignments || manifestData.consignments.length === 0) {
      alert('No data to export');
      return;
    }
    
    // CSV Headers
    const headers = [
      'Consignment No',
      'Date',
      'Shipper',
      'Consignee',
      'Origin',
      'Destination',
      'Weight (kg)',
      'Packages',
      'Vehicle',
      'Driver',
      'Status'
    ];
    
    // CSV Rows
    const rows = manifestData.consignments.map(c => [
      c.ConsignmentNo || '',
      new Date(c.ConsignmentDate).toLocaleDateString(),
      c.ShipperName || '',
      c.ConsigneeName || '',
      c.OriginCity || '',
      c.DestinationCity || '',
      c.Weight || '0',
      c.NoOfPkgs || '0',
      c.VehicleNo || '',
      c.DriverName || '',
      c.Status || ''
    ]);
    
    // Combine
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `manifest_${filters.StartDate}_to_${filters.EndDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const handleClearFilters = () => {
    setFilters({
      StartDate: new Date().toISOString().slice(0, 10),
      EndDate: new Date().toISOString().slice(0, 10),
      VehicleID: '',
      DriverID: '',
      OriginCityID: '',
      DestinationCityID: '',
      Status: '',
      ConsignmentType: 'LT'
    });
    setManifestData(null);
  };
  
  if (!session?.companyid) {
    return (
      <div 
        className="invoice-form-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            if (onClose) {
              onClose();
            } else {
              navigate('/dashboard');
            }
          }
        }}
      >
        <div className="invoice-form-container" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
          <div className="form-header">
            <h2>Session Required</h2>
            <button 
              className="close-btn-fixed" 
              onClick={(e) => {
                e.stopPropagation();
                if (onClose) {
                  onClose();
                } else {
                  navigate('/dashboard');
                }
              }}
              type="button"
              title="Close"
            >
              <X size={20} />
            </button>
          </div>
          <div style={{ padding: '24px', textAlign: 'center' }}>
            <p>Please select a company and financial year from the dashboard.</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      className="invoice-form-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          if (onClose) {
            onClose();
          } else {
            navigate('/dashboard');
          }
        }
      }}
    >
      <div className="invoice-form-container manifest-report-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="form-header" style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '28px 32px',
          borderRadius: '12px 12px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '4px solid #5a67d8',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          position: 'relative'
        }}>
          <div className="header-left" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '24px' 
          }}>
            {/* Icon Container */}
            <div className="no-print" style={{
              width: '60px',
              height: '60px',
              background: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              flexShrink: 0,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
            }}>
              <FileText size={32} />
            </div>
            
            {/* Title Container */}
            <div style={{ flex: 1 }}>
              <h2 className="manifest-header-title" id="manifest-title" style={{ 
                fontSize: '2.2rem',
                fontWeight: '900',
                margin: 0,
                textShadow: '0 3px 8px rgba(0, 0, 0, 0.8), 0 6px 16px rgba(0, 0, 0, 0.6), 0 1px 0 rgba(0, 0, 0, 1), 0 0 20px rgba(0, 0, 0, 0.5)',
                lineHeight: '1.4',
                letterSpacing: '1.5px',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
                filter: 'drop-shadow(0 2px 6px rgba(0, 0, 0, 0.6))',
                color: 'white',
                WebkitTextFillColor: 'white'
              }}>
                <span style={{ color: 'white', WebkitTextFillColor: 'white' }}>Manifest Report - Land Transport</span>
              </h2>
            </div>
          </div>
          
          <button 
            className="close-btn-fixed no-print"
            onClick={(e) => {
              e.stopPropagation();
              if (onClose) {
                onClose();
              } else {
                navigate('/dashboard');
              }
            }}
            type="button"
            title="Close (ESC)"
            style={{ 
              background: 'rgba(255, 255, 255, 0.2)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              color: 'white',
              width: '44px',
              height: '44px',
              borderRadius: '10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
              e.currentTarget.style.transform = 'rotate(90deg) scale(1.05)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
              e.currentTarget.style.transform = 'none';
            }}
          >
            <X size={22} />
          </button>
        </div>
        
        {/* ABSOLUTE NUCLEAR FIX - CANNOT BE OVERRIDDEN */}
        <style dangerouslySetInnerHTML={{__html: `
          /* Ultra high specificity - beats everything */
          html body div.manifest-report-container div.form-header h2,
          html body div.manifest-report-container div.form-header h2 span,
          html body div.manifest-report-container div.form-header h2 *,
          html body .manifest-report-container .form-header h2,
          html body .manifest-report-container .form-header h2 *,
          body .manifest-report-container .form-header h2,
          body .manifest-report-container .form-header h2 *,
          #manifest-title,
          #manifest-title span,
          #manifest-title *,
          .manifest-header-title,
          .manifest-header-title span,
          .manifest-header-title *,
          .manifest-report-container .form-header h2,
          .manifest-report-container .form-header h2 span,
          .manifest-report-container .form-header h2 *,
          .manifest-report-container .form-header *,
          .form-header h2,
          .form-header h2 span,
          .form-header h2 *,
          .form-header * {
            color: #ffffff !important;
            -webkit-text-fill-color: #ffffff !important;
            text-shadow: 0 3px 8px rgba(0, 0, 0, 0.8), 0 6px 16px rgba(0, 0, 0, 0.6), 0 1px 0 rgba(0, 0, 0, 1), 0 0 20px rgba(0, 0, 0, 0.5) !important;
          }
          
          /* Force on all header descendants */
          .manifest-report-container .form-header,
          .manifest-report-container .form-header > *,
          .manifest-report-container .form-header > * > *,
          .manifest-report-container .form-header > * > * > * {
            color: #ffffff !important;
            -webkit-text-fill-color: #ffffff !important;
          }
        `}} />
        
        {/* Toolbar */}
        <div className="invoice-toolbar manifest-toolbar no-print">
          <button onClick={handleGenerateReport} disabled={loading} className="btn-toolbar btn-save">
            <Search size={18} />
            Generate Report
          </button>
          <button 
            onClick={handlePrint} 
            disabled={!manifestData || loading}
            className="btn-toolbar"
          >
            <Printer size={18} />
            Print
          </button>
          <button 
            onClick={handleExportCSV} 
            disabled={!manifestData || loading}
            className="btn-toolbar"
          >
            <Download size={18} />
            Export CSV
          </button>
          <button onClick={handleClearFilters} disabled={loading} className="btn-toolbar">
            <RefreshCw size={18} />
            Clear Filters
          </button>
        </div>
        
        {/* Alerts */}
        {error && (
          <div className="alert alert-error no-print">
            <strong>Error:</strong> {error}
          </div>
        )}
        
        <div className="form-content">
          {/* Filters */}
          <div className="form-section no-print">
            <h3 className="section-title">
              <Filter size={18} />
              Report Filters
            </h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Start Date *</label>
                <input
                  type="date"
                  name="StartDate"
                  value={filters.StartDate}
                  onChange={handleFilterChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>End Date *</label>
                <input
                  type="date"
                  name="EndDate"
                  value={filters.EndDate}
                  onChange={handleFilterChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Consignment Type</label>
                <select
                  name="ConsignmentType"
                  value={filters.ConsignmentType}
                  onChange={handleFilterChange}
                >
                  <option value="">All Types</option>
                  <option value="LT">Land Transport (LT)</option>
                  <option value="AT">Air Transport (AT)</option>
                  <option value="ST">Sea Transport (ST)</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Vehicle</label>
                <select
                  name="VehicleID"
                  value={filters.VehicleID}
                  onChange={handleFilterChange}
                >
                  <option value="">All Vehicles</option>
                  {vehicles.map(veh => (
                    <option key={veh.VehicleID} value={veh.VehicleID}>
                      {veh.RegistrationNo} {veh.VehicleType && `- ${veh.VehicleType}`}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Driver</label>
                <select
                  name="DriverID"
                  value={filters.DriverID}
                  onChange={handleFilterChange}
                >
                  <option value="">All Drivers</option>
                  {drivers.map(driver => (
                    <option key={driver.driverid} value={driver.driverid}>
                      {driver.drivername}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Origin City</label>
                <select
                  name="OriginCityID"
                  value={filters.OriginCityID}
                  onChange={handleFilterChange}
                >
                  <option value="">All Origins</option>
                  {cities.map(city => (
                    <option key={city.cityid} value={city.cityid}>
                      {city.cityname}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Destination City</label>
                <select
                  name="DestinationCityID"
                  value={filters.DestinationCityID}
                  onChange={handleFilterChange}
                >
                  <option value="">All Destinations</option>
                  {cities.map(city => (
                    <option key={city.cityid} value={city.cityid}>
                      {city.cityname}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Status</label>
                <select
                  name="Status"
                  value={filters.Status}
                  onChange={handleFilterChange}
                >
                  <option value="">All Statuses</option>
                  {courierStatuses.map(status => (
                    <option key={status.CourierStatusID} value={status.CourierStatusID}>
                      {status.StatusName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          {/* Report Display */}
          {loading && (
            <div className="form-section">
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div className="spinner" style={{ margin: '0 auto 16px' }}></div>
                <p>Generating manifest report...</p>
              </div>
            </div>
          )}
          
          {manifestData && !loading && (
            <>
              {/* Report Header */}
              <div className="form-section print-header" id="print-header" style={{ pageBreakAfter: 'avoid' }}>
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <h2 style={{ margin: '0 0 8px 0', color: '#1f2937', fontSize: '22px' }}>
                    {manifestData.company?.CompanyName || 'Company Name'}
                  </h2>
                  {manifestData.company?.Address && (
                    <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>
                      {manifestData.company.Address}
                    </div>
                  )}
                  <h3 style={{ margin: '0 0 16px 0', color: '#4b5563', fontWeight: '600', fontSize: '18px' }}>
                    Manifest Report - Land Transport
                  </h3>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>
                    <div><strong>Period:</strong> {new Date(filters.StartDate).toLocaleDateString('en-GB')} to {new Date(filters.EndDate).toLocaleDateString('en-GB')}</div>
                    <div><strong>Generated:</strong> {new Date().toLocaleString('en-GB')}</div>
                  </div>
                </div>
                
                {/* Summary Cards */}
                <div className="summary-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
                  <div className="summary-card" style={{ background: '#eff6ff', padding: '16px', borderRadius: '8px', border: '1px solid #dbeafe' }}>
                    <div style={{ fontSize: '12px', color: '#1e40af', fontWeight: '600', marginBottom: '4px' }}>
                      Total Consignments
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e3a8a' }}>
                      {manifestData.summary?.totalConsignments || 0}
                    </div>
                  </div>
                  
                  <div className="summary-card" style={{ background: '#f0fdf4', padding: '16px', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                    <div style={{ fontSize: '12px', color: '#15803d', fontWeight: '600', marginBottom: '4px' }}>
                      Total Weight (kg)
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#166534' }}>
                      {parseFloat(manifestData.summary?.totalWeight || 0).toFixed(2)}
                    </div>
                  </div>
                  
                  <div className="summary-card" style={{ background: '#fef3c7', padding: '16px', borderRadius: '8px', border: '1px solid #fde68a' }}>
                    <div style={{ fontSize: '12px', color: '#92400e', fontWeight: '600', marginBottom: '4px' }}>
                      Total Packages
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#78350f' }}>
                      {manifestData.summary?.totalPackages || 0}
                    </div>
                  </div>
                  
                  <div className="summary-card" style={{ background: '#fce7f3', padding: '16px', borderRadius: '8px', border: '1px solid #fbcfe8' }}>
                    <div style={{ fontSize: '12px', color: '#9f1239', fontWeight: '600', marginBottom: '4px' }}>
                      Vehicles Used
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#881337' }}>
                      {manifestData.summary?.vehiclesUsed || 0}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Filter Summary */}
              {(filters.VehicleID || filters.DriverID || filters.OriginCityID || filters.DestinationCityID) && (
                <div className="form-section no-print" style={{ background: '#f9fafb', padding: '12px 16px' }}>
                  <div style={{ fontSize: '13px', color: '#4b5563' }}>
                    <strong>Applied Filters:</strong>
                    {filters.VehicleID && (
                      <span style={{ marginLeft: '8px', background: '#e5e7eb', padding: '2px 8px', borderRadius: '4px' }}>
                        Vehicle: {vehicles.find(v => v.VehicleID === filters.VehicleID)?.RegistrationNo}
                      </span>
                    )}
                    {filters.DriverID && (
                      <span style={{ marginLeft: '8px', background: '#e5e7eb', padding: '2px 8px', borderRadius: '4px' }}>
                        Driver: {drivers.find(d => d.driverid === filters.DriverID)?.drivername}
                      </span>
                    )}
                    {filters.OriginCityID && (
                      <span style={{ marginLeft: '8px', background: '#e5e7eb', padding: '2px 8px', borderRadius: '4px' }}>
                        Origin: {cities.find(c => c.cityid === filters.OriginCityID)?.cityname}
                      </span>
                    )}
                    {filters.DestinationCityID && (
                      <span style={{ marginLeft: '8px', background: '#e5e7eb', padding: '2px 8px', borderRadius: '4px' }}>
                        Destination: {cities.find(c => c.cityid === filters.DestinationCityID)?.cityname}
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Consignments Table */}
              <div className="form-section consignment-table-section" style={{ pageBreakInside: 'auto' }}>
                <h3 className="section-title" style={{ marginTop: '0', pageBreakAfter: 'avoid' }}>
                  <Package size={18} />
                  Consignment Details
                </h3>
                
                {manifestData.consignments && manifestData.consignments.length > 0 ? (
                  <div className="invoice-items-table" style={{ marginTop: '16px', overflow: 'visible' }}>
                    <table className="data-table manifest-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ width: '100px', fontSize: '11px', padding: '8px 4px', textAlign: 'left', border: '1px solid #e5e7eb' }}>Consignment No</th>
                          <th style={{ width: '80px', fontSize: '11px', padding: '8px 4px', textAlign: 'left', border: '1px solid #e5e7eb' }}>Date</th>
                          <th style={{ fontSize: '11px', padding: '8px 4px', textAlign: 'left', border: '1px solid #e5e7eb' }}>Shipper</th>
                          <th style={{ fontSize: '11px', padding: '8px 4px', textAlign: 'left', border: '1px solid #e5e7eb' }}>Consignee</th>
                          <th style={{ fontSize: '11px', padding: '8px 4px', textAlign: 'left', border: '1px solid #e5e7eb' }}>Route</th>
                          <th className="text-right" style={{ width: '70px', fontSize: '11px', padding: '8px 4px', textAlign: 'right', border: '1px solid #e5e7eb' }}>Weight (kg)</th>
                          <th className="text-right" style={{ width: '60px', fontSize: '11px', padding: '8px 4px', textAlign: 'right', border: '1px solid #e5e7eb' }}>Pkgs</th>
                          <th style={{ width: '100px', fontSize: '11px', padding: '8px 4px', textAlign: 'left', border: '1px solid #e5e7eb' }}>Vehicle</th>
                          <th style={{ width: '100px', fontSize: '11px', padding: '8px 4px', textAlign: 'left', border: '1px solid #e5e7eb' }}>Driver</th>
                          <th style={{ width: '80px', fontSize: '11px', padding: '8px 4px', textAlign: 'left', border: '1px solid #e5e7eb' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {manifestData.consignments.map((consignment, index) => (
                          <tr key={consignment.ConsignmentID || index} style={{ pageBreakInside: 'avoid' }}>
                            <td style={{ fontFamily: 'monospace', fontWeight: '600', fontSize: '11px', padding: '6px 4px', border: '1px solid #e5e7eb' }}>
                              {consignment.ConsignmentNo}
                            </td>
                            <td style={{ fontSize: '11px', padding: '6px 4px', border: '1px solid #e5e7eb' }}>
                              {new Date(consignment.ConsignmentDate).toLocaleDateString('en-GB')}
                            </td>
                            <td style={{ border: '1px solid #e5e7eb', padding: '6px 4px' }}>
                              <div style={{ fontSize: '11px' }}>
                                <div style={{ fontWeight: '500' }}>{consignment.ShipperName || '-'}</div>
                                {consignment.ShipperPhone && (
                                  <div style={{ color: '#6b7280', fontSize: '10px' }}>{consignment.ShipperPhone}</div>
                                )}
                              </div>
                            </td>
                            <td style={{ border: '1px solid #e5e7eb', padding: '6px 4px' }}>
                              <div style={{ fontSize: '11px' }}>
                                <div style={{ fontWeight: '500' }}>{consignment.ConsigneeName || '-'}</div>
                                {consignment.ConsigneePhone && (
                                  <div style={{ color: '#6b7280', fontSize: '10px' }}>{consignment.ConsigneePhone}</div>
                                )}
                              </div>
                            </td>
                            <td style={{ fontSize: '11px', padding: '6px 4px', border: '1px solid #e5e7eb' }}>
                              {consignment.OriginCity || 'N/A'} → {consignment.DestinationCity || 'N/A'}
                            </td>
                            <td className="text-right" style={{ fontWeight: '600', fontSize: '11px', padding: '6px 4px', textAlign: 'right', border: '1px solid #e5e7eb' }}>
                              {parseFloat(consignment.Weight || 0).toFixed(2)}
                            </td>
                            <td className="text-right" style={{ fontSize: '11px', padding: '6px 4px', textAlign: 'right', border: '1px solid #e5e7eb' }}>
                              {consignment.NoOfPkgs || 0}
                            </td>
                            <td style={{ fontSize: '11px', padding: '6px 4px', border: '1px solid #e5e7eb' }}>
                              {consignment.VehicleNo || '-'}
                            </td>
                            <td style={{ fontSize: '11px', padding: '6px 4px', border: '1px solid #e5e7eb' }}>
                              {consignment.DriverName || '-'}
                            </td>
                            <td style={{ padding: '6px 4px', border: '1px solid #e5e7eb' }}>
                              <span style={{
                                padding: '3px 6px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: '500',
                                background: getStatusColor(consignment.Status).bg,
                                color: getStatusColor(consignment.Status).text,
                                WebkitPrintColorAdjust: 'exact',
                                printColorAdjust: 'exact'
                              }}>
                                {consignment.Status || 'Pending'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: '#f9fafb', fontWeight: 'bold', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
                          <td colSpan="5" style={{ textAlign: 'right', paddingRight: '12px', fontSize: '12px', padding: '8px 12px', border: '1px solid #e5e7eb' }}>
                            Totals:
                          </td>
                          <td className="text-right" style={{ color: '#667eea', fontSize: '14px', fontWeight: 'bold', padding: '8px 4px', textAlign: 'right', border: '1px solid #e5e7eb' }}>
                            {manifestData.consignments.reduce((sum, c) => sum + parseFloat(c.Weight || 0), 0).toFixed(2)}
                          </td>
                          <td className="text-right" style={{ color: '#667eea', fontSize: '14px', fontWeight: 'bold', padding: '8px 4px', textAlign: 'right', border: '1px solid #e5e7eb' }}>
                            {manifestData.consignments.reduce((sum, c) => sum + parseInt(c.NoOfPkgs || 0), 0)}
                          </td>
                          <td colSpan="3" style={{ border: '1px solid #e5e7eb' }}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                    <Package size={48} style={{ opacity: 0.3, margin: '0 auto 16px' }} />
                    <p>No consignments found for the selected filters</p>
                  </div>
                )}
              </div>
              
              {/* Signature Section (for print) */}
              <div className="form-section print-only" style={{ marginTop: '60px', pageBreakInside: 'avoid', border: 'none' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '60px', paddingTop: '40px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ borderTop: '2px solid #000', paddingTop: '10px', marginTop: '50px', fontWeight: '600' }}>
                      Prepared By
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ borderTop: '2px solid #000', paddingTop: '10px', marginTop: '50px', fontWeight: '600' }}>
                      Checked By
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ borderTop: '2px solid #000', paddingTop: '10px', marginTop: '50px', fontWeight: '600' }}>
                      Approved By
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Print Styles */}
      <style jsx>{`
        /* NUCLEAR WHITE TEXT FIX - Ultra high specificity */
        html body .invoice-form-overlay .invoice-form-container .form-header h2,
        html body .invoice-form-overlay .invoice-form-container .form-header .header-left h2,
        html body .invoice-form-overlay .invoice-form-container .form-header .manifest-header-title,
        html body .invoice-form-overlay .invoice-form-container .form-header .manifest-header-title span,
        html body .invoice-form-overlay .invoice-form-container .form-header h2 span,
        .invoice-form-container .form-header h2,
        .invoice-form-container .form-header .header-left h2,
        .invoice-form-container .form-header .manifest-header-title,
        .invoice-form-container .form-header .manifest-header-title span,
        .manifest-header-title,
        .manifest-header-title span,
        .form-header h2,
        .form-header .header-left h2 {
          color: #ffffff !important;
          text-shadow: 0 3px 8px rgba(0, 0, 0, 0.8), 
                       0 6px 16px rgba(0, 0, 0, 0.6), 
                       0 1px 0 rgba(0, 0, 0, 1), 
                       0 0 20px rgba(0, 0, 0, 0.5) !important;
        }
        
        /* Force white on ALL elements inside header */
        .form-header,
        .form-header *,
        .form-header h1,
        .form-header h2,
        .form-header h3,
        .form-header span,
        .form-header div {
          color: #ffffff !important;
        }
        
        @media print {
          /* Hide non-essential elements */
          .no-print {
            display: none !important;
          }
          
          /* Show print-only elements */
          .print-only {
            display: block !important;
          }
          
          /* Fix overlay and container for print */
          .invoice-form-overlay {
            position: static !important;
            background: white !important;
            padding: 0 !important;
            overflow: visible !important;
          }
          
          .invoice-form-container {
            max-height: none !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            overflow: visible !important;
          }
          
          .form-header {
            background: white !important;
            color: #1f2937 !important;
            border-bottom: 2px solid #000 !important;
            page-break-after: avoid !important;
            padding: 10px 0 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          .form-header h2 {
            color: #1f2937 !important;
            font-size: 18px !important;
            text-align: center !important;
            width: 100% !important;
          }
          
          .form-header .header-left {
            width: 100% !important;
            justify-content: center !important;
          }
          
          /* Print header section */
          .print-header {
            display: block !important;
            visibility: visible !important;
            page-break-after: avoid !important;
          }
          
          .form-content {
            overflow: visible !important;
            padding: 0 !important;
          }
          
          /* Form sections */
          .form-section {
            page-break-inside: auto !important;
            border: none !important;
            box-shadow: none !important;
            margin-bottom: 10px !important;
            display: block !important;
            visibility: visible !important;
          }
          
          /* Consignment table section - CRITICAL */
          .consignment-table-section {
            display: block !important;
            visibility: visible !important;
            page-break-inside: auto !important;
          }
          
          /* Invoice items table container */
          .invoice-items-table {
            display: block !important;
            visibility: visible !important;
            overflow: visible !important;
            border: none !important;
          }
          
          /* Table styling for print */
          .data-table,
          .manifest-table {
            font-size: 9px !important;
            width: 100% !important;
            display: table !important;
            visibility: visible !important;
            border-collapse: collapse !important;
          }
          
          .data-table thead,
          .manifest-table thead {
            display: table-header-group !important;
            visibility: visible !important;
            background: #f3f4f6 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          .data-table tbody,
          .manifest-table tbody {
            display: table-row-group !important;
            visibility: visible !important;
          }
          
          .data-table th,
          .data-table td,
          .manifest-table th,
          .manifest-table td {
            padding: 4px 3px !important;
            border: 1px solid #d1d5db !important;
            font-size: 9px !important;
            display: table-cell !important;
            visibility: visible !important;
          }
          
          .data-table th {
            background: #f3f4f6 !important;
            font-weight: bold !important;
          }
          
          .data-table tr,
          .manifest-table tr {
            display: table-row !important;
            visibility: visible !important;
            page-break-inside: avoid !important;
          }
          
          /* Section titles */
          .section-title {
            display: flex !important;
            visibility: visible !important;
            page-break-after: avoid !important;
            margin-bottom: 10px !important;
            font-size: 14px !important;
            font-weight: bold !important;
            border-bottom: 2px solid #667eea !important;
            padding-bottom: 8px !important;
          }
          
          /* Summary cards for print */
          .summary-cards {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            page-break-inside: avoid !important;
          }
          
          .summary-card {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Status badges */
          span[style*="padding: 4px 8px"] {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Ensure tfoot prints */
          .data-table tfoot {
            display: table-footer-group !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Page breaks */
          @page {
            size: A4 landscape;
            margin: 0.75cm;
          }
          
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          h2, h3 {
            page-break-after: avoid !important;
          }
          
          tr {
            page-break-inside: avoid !important;
          }
          
          /* Force visibility of all content */
          * {
            visibility: visible !important;
          }
          
          .no-print {
            display: none !important;
            visibility: hidden !important;
          }
        }
        
        .print-only {
          display: none;
        }
        
        .spinner {
          border: 3px solid #f3f4f6;
          border-top: 3px solid #667eea;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      
      {/* NUCLEAR OPTION - Regular style tag with maximum specificity */}
      <style dangerouslySetInnerHTML={{__html: `
        html body .invoice-form-overlay .invoice-form-container .form-header h2,
        html body .invoice-form-overlay .invoice-form-container .form-header .manifest-header-title,
        html body .invoice-form-overlay .invoice-form-container .form-header span,
        .invoice-form-container .form-header h2,
        .invoice-form-container .form-header .manifest-header-title,
        .invoice-form-container .form-header span,
        .form-header h2,
        .form-header .manifest-header-title,
        .form-header span,
        .manifest-header-title,
        .manifest-header-title span {
          color: #ffffff !important;
          text-shadow: 0 3px 8px rgba(0, 0, 0, 0.8) !important;
        }
        
        .form-header,
        .form-header * {
          color: #ffffff !important;
        }
      `}} />
    </div>
  );
};

function getStatusColor(status) {
  const colors = {
    'Pending': { bg: '#fef3c7', text: '#92400e' },
    'In Transit': { bg: '#dbeafe', text: '#1e40af' },
    'Delivered': { bg: '#d1fae5', text: '#065f46' },
    'Returned': { bg: '#fee2e2', text: '#991b1b' }
  };
  return colors[status] || { bg: '#f3f4f6', text: '#374151' };
}

export default ManifestReport;